// @flow
import React, { useContext, useState, Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Formik, Form } from 'formik';
import { Redirect, withRouter, Link } from 'react-router-dom';

import Box from 'bizom-mui/lib/Box';
import TextLink from 'bizom-mui/lib/TextLink';

import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { SessionContext } from 'session';
import { APP_LOGO_PATH, LOGIN_BACKGROUND_PATH } from 'constants/assets';
import * as paths from 'constants/paths';
import {
  PROCEED,
  MOBILE_NUMBER,
  TERMS_AND_CONDITIONS,
  PLEASE_ENTER_YOUR_MOBILE_NUMBER,
  BY_CLICKING_PROCEED_YOU_AGREE_TO_OUR,
} from 'constants/strings';
import {
  ERROR_WHILE_LOGGING_IN,
  MOBILE_NUMBER_IS_REQUIRED,
  THIS_NUMBER_IS_NOT_REGISTERED,
  PLEASE_ENTER_VALID_MOBILE_NUMBER,
} from 'constants/errors';
import { APP_NAME, PRODUCT_NAME, PRODUCT_NAME_ALIAS } from 'constants/enums';

import StyledCheckbox from './StyledCheckbox';
import FormikPhoneField from 'components/FormikPhoneField';
import { SnackbarContext } from 'contexts/SnackbarContext';
import LanguageSelector from 'components/LanguageSelector';

import { toRawPhoneNumber } from 'utils/toRawPhoneNumber';
import { getDefaultCountry } from 'utils/getDefaultCountry';
import { isValidPhoneNumber } from 'utils/isValidPhoneNumber';

type LoginFormProps = {
  classes: any,
  loading: boolean,
  history: any,
  onSubmit: ({ phoneno: string, dialCode: string }) => Promise<any>,
};

const styles = ({ extras, spacing, palette, shadows }) => ({
  coverWrapper: {
    width: '100%',
    top: 0,
    left: 0,
    position: 'fixed',
    height: extras.sizing.unit * 5,
  },
  cover: {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
  },
  content: {
    position: 'relative',
    backgroundColor: palette.background.default,
    height: `calc(100vh - ${extras.sizing.unit * 4}px)`,
    marginTop: spacing(16),
    borderRadius: spacing(2, 2, 0, 0),
    boxShadow: `0px -3px 4px 0px rgba(0,0,0,0.12)`,
  },
  logoWrapper: {
    backgroundColor: palette.background.paper,
    height: extras.sizing.unit * 3,
    width: extras.sizing.unit * 3,
    borderRadius: '50%',
    position: 'absolute',
    top: spacing(-6),
    left: 0,
    right: 0,
    margin: 'auto',
    padding: spacing(2),
    boxShadow: shadows[3],
  },
  logo: {
    height: extras.sizing.unit * 2,
    width: extras.sizing.unit * 2,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    padding: spacing(1),
    width: '100%',
  },
  tncCheckbox: {
    marginRight: 0,
  },
});

const LoginForm = ({ classes, history, loading, onSubmit }: LoginFormProps) => {
  const [disable, setDisable] = useState(true);
  const [checked, setChecked] = useState(true);
  const { session } = useContext(SessionContext);
  const { openSnackbar } = useContext(SnackbarContext);

  if (session && session.token) {
    return <Redirect to={paths.HOME} />;
  }

  return (
    <Fragment>
      <div className={classes.coverWrapper}>
        <img
          alt="Cover"
          src={LOGIN_BACKGROUND_PATH}
          className={classes.cover}
        />
      </div>
      <div className={classes.content}>
        <div className={classes.logoWrapper}>
          <img alt="Logo" src={APP_LOGO_PATH} className={classes.logo} />
        </div>
        <Formik
          initialValues={{
            phoneno: '',
            dialCode: '',
          }}
          validate={({ dialCode, phoneno }) => {
            let errors = {};
            if (!dialCode || !phoneno || !toRawPhoneNumber(phoneno, dialCode)) {
              setDisable(true);
              errors = {
                ...errors,
                phoneno: MOBILE_NUMBER_IS_REQUIRED,
              };
            } else if (disable) {
              setDisable(false);
            }
            return errors;
          }}
          onSubmit={(values, actions) => {
            const phoneno = toRawPhoneNumber(values.phoneno, values.dialCode);

            if (!isValidPhoneNumber(values.dialCode, phoneno)) {
              setDisable(true);
              actions.setSubmitting(false);
              actions.setFieldError(
                'phoneno',
                PLEASE_ENTER_VALID_MOBILE_NUMBER
              );
              return;
            }

            onSubmit({ phoneno: `${phoneno}`, dialCode: values.dialCode })
              .then((response) => {
                if (response && response.data.otpRequest.response) {
                  localStorage.setItem('phoneno', values.phoneno);
                  localStorage.setItem('dialCode', values.dialCode);
                  localStorage.setItem(
                    'resend-otp-state',
                    JSON.stringify({
                      count: 0,
                      ts: new Date().getTime(),
                    })
                  );
                  history.push(paths.LOGIN_VERIFY_USER);
                } else {
                  openSnackbar &&
                    openSnackbar({
                      id: 'cy-error-on-otp-resquest',
                      message: ERROR_WHILE_LOGGING_IN,
                      autoHide: true,
                    });
                  actions.setSubmitting(false);
                }
              })
              .catch((err) => {
                if (
                  err &&
                  err.message.includes('Phone number is not Registered')
                ) {
                  openSnackbar &&
                    openSnackbar({
                      id: 'cy-error-on-otp-resquest',
                      message: THIS_NUMBER_IS_NOT_REGISTERED,
                      autoHide: true,
                    });
                }
                actions.setSubmitting(false);
              });
          }}
          autoComplete="off"
        >
          <Form>
            <Box pt={8} px={2}>
              <Typography variant="h5" align="center" gutterBottom>
                {PRODUCT_NAME_ALIAS[APP_NAME]}
              </Typography>
              <Box pt={2}>
                <Typography
                  gutterBottom
                  variant="body2"
                  align="center"
                  color="textSecondary"
                >
                  {PLEASE_ENTER_YOUR_MOBILE_NUMBER}
                </Typography>
              </Box>
              <Box px={2}>
                <FormikPhoneField
                  type="tel"
                  variant="outlined"
                  name="phoneno"
                  defaultCountry={getDefaultCountry()}
                  dialCodeFieldName="dialCode"
                  id="cy-login-form-phoneno"
                  label={MOBILE_NUMBER}
                  margin="normal"
                  fullWidth
                />
              </Box>
              <Box pt={2} px={2}>
                <Button
                  fullWidth
                  size="large"
                  id="gtm-button-proceed-login"
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={disable || loading || !checked}
                >
                  {PROCEED}
                </Button>
              </Box>
            </Box>
          </Form>
        </Formik>
        {process.env.REACT_APP_RETAILER_APP_TNC_URL && (
          <Box pt={2} px={2} align="center">
            <FormControlLabel
              className={classes.tncCheckbox}
              control={
                <StyledCheckbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  name="tnc-checkbox"
                />
              }
              label={
                <Typography
                  variant="caption"
                  display="inline"
                  color="textSecondary"
                >
                  {BY_CLICKING_PROCEED_YOU_AGREE_TO_OUR}
                </Typography>
              }
            />
            <Box ml={1} inline>
              <TextLink
                id="link-signup"
                color="primary"
                component={Link}
                to={paths.TERMS_AND_CONDITIONS}
              >
                <Typography variant="caption" color="primary" display="inline">
                  {TERMS_AND_CONDITIONS}
                </Typography>
              </TextLink>
            </Box>
          </Box>
        )}
        {process.env.REACT_APP_RETAILER_APP_APP_NAME !== PRODUCT_NAME.BIDCO && (
          <Box mt={2} px={2}>
            <Divider variant="middle" />
            <Box pt={2}>
              <LanguageSelector />
            </Box>
          </Box>
        )}
      </div>
    </Fragment>
  );
};

export default withRouter(withStyles(styles)(LoginForm));
